<template>
  <div class="tranzila-hf" dir="rtl">
    <div class="d-flex flex-row">
      <button type="button" class="f20-l30-b pointer popup-close-btn" @click="onCancel" aria-label="סגור">X</button>
      <h2 :id="popupTitleId" class="f20-l30-b flex-grow-1 text-center heading-reset" v-html="content.title"></h2>
    </div>
    <div class="f14-l17 flex-grow-1" v-html="content.subtitle"></div>
    <div class="f14-l17 red" id="popup-error-message" role="alert" aria-live="polite">{{ popupError }}</div>
    <div class="f14-l17 red" id="popup-error-message-description"></div>
    <LoadingIndicator dir="ltr" v-if="loading && !verifyingPayment" />

    <div
      v-if="verifyingPayment"
      class="text-center margin-tb-20"
      role="status"
      aria-live="polite"
    >
      <LoadingIndicator dir="ltr" />
      <div class="f14-l17">{{ verifyingMessage }}</div>
    </div>

    <div v-else-if="blockedMessage" class="f14-l17 red text-center margin-tb-20" role="alert">{{ blockedMessage }}</div>

    <form
      v-else
      ref="hfForm"
      class="formfield-round-border formfield-16 tranzila-hf-form needs-validation"
      dir="rtl"
      novalidate
      @submit.prevent="onCardSubmit"
    >
      <div class="tranzila-hf-card-fields" :class="{ 'tranzila-hf-card-fields-inert': !isCard }" :aria-hidden="isCard ? 'false' : 'true'">
        <div class="form-group row form-group-border-bottom align-items-center">
          <label :for="numberFieldId" class="col-form-label col-auto">
            <span v-if="hostedFieldRequired('credit_card_number')" class="required">*</span>
            {{ hostedFieldLabel("credit_card_number", "מספר כרטיס") }}
          </label>
          <div class="col">
            <div
              :id="numberFieldId"
              class="tranzila-hf-field"
              :class="fieldClass('credit_card_number')"
            ></div>
            <div id="errors_for_number" class="f14-l17 red tranzila-hf-error" role="alert">{{ fieldErrors.number }}</div>
            <div id="errors_for_credit_card_number" class="f14-l17 red tranzila-hf-error" role="alert">{{ fieldErrors.credit_card_number }}</div>
          </div>
        </div>
        <div class="form-group row form-group-border-bottom align-items-center">
          <label :for="expiryFieldId" class="col-form-label col-auto">
            <span v-if="hostedFieldRequired('expiry')" class="required">*</span>
            {{ hostedFieldLabel("expiry", "תוקף") }}
          </label>
          <div class="col">
            <div
              :id="expiryFieldId"
              class="tranzila-hf-field"
              :class="fieldClass('expiry')"
            ></div>
            <div id="errors_for_expiry" class="f14-l17 red tranzila-hf-error" role="alert">{{ fieldErrors.expiry }}</div>
          </div>
        </div>
        <div class="form-group row form-group-border-bottom align-items-center">
          <label :for="cvvFieldId" class="col-form-label col-auto">
            <span v-if="hostedFieldRequired('cvv')" class="required">*</span>
            {{ hostedFieldLabel("cvv", "CVV") }}
          </label>
          <div class="col">
            <div
              :id="cvvFieldId"
              class="tranzila-hf-field"
              :class="fieldClass('cvv')"
            ></div>
            <div id="errors_for_cvv" class="f14-l17 red tranzila-hf-error" role="alert">{{ fieldErrors.cvv }}</div>
          </div>
        </div>
        <FormFieldComponent
          v-for="(field, idx) in extraFormFields"
          :key="field.data_name || 'hf_field_' + idx"
          :field="popupTextField(field)"
        />
        <ButtonComponent
          v-if="isCard"
          caption="תשלום בכרטיס אשראי"
          class="margin-top-20"
          :busy="busy"
          :disabled="busy || chargeLocked"
          isSubmitButton
        />
      </div>
      <div v-if="!isCard" class="margin-top-20 tranzila-hf-wallet">
        <div v-if="showWalletWait" class="f14-l17 text-center">{{ waitMessage }}</div>
        <div v-if="showGoogleUnavailable" class="f14-l17 red text-center" role="alert">{{ googleUnavailableMessage }}</div>
        <ButtonComponent
          v-if="showApplePayButton"
          caption="תשלום ב-Apple Pay"
          class="margin-top-20"
          :busy="busy"
          :disabled="busy || chargeLocked"
          @click="onApplePayClick"
        />
        <ButtonComponent
          v-if="showGooglePayButton"
          caption="תשלום ב-Google Pay"
          class="margin-top-20"
          :busy="busy"
          :disabled="busy || chargeLocked"
          @click="onGooglePayClick"
        />
        <ButtonComponent
          v-if="walletRetryAvailable"
          caption="נסה שוב"
          class="margin-top-20"
          :busy="busy"
          :disabled="busy || chargeLocked"
          @click="retryWalletCharge"
        />
      </div>
    </form>
    <div v-if="iconsStrip.length" class="tranzila-icons-strip" dir="ltr">
      <img
        v-for="(icon, index) in iconsStrip"
        :key="(icon.id || 'icon') + '-' + index"
        class="tranzila-icons-strip-img"
        :src="icon.src"
        :alt="typeof icon.alt === 'string' ? icon.alt : ''"
        draggable="false"
      />
    </div>
  </div>
</template>
<script>
import ButtonComponent from "@/components/form/ButtonComponent";
import FormFieldComponent from "@/components/form/FormFieldComponent";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import { readCardHolderIdFromForm } from "@/modules/israeliId.js";
import {
  CONTACT_SUPPORT_HE,
  CONFIRM_FAILED_HE,
  CHARGE_FAILED_HE,
  HANDSHAKE_RETRY_HE,
  MISSING_FIELDS_HE,
  METHOD_NOT_ALLOWED_HE,
  PAYMENT_VERIFYING_HE,
  canChargeHostedFields,
  chargeParamsForMethod,
  clearTranzilaPendingCharge,
  confirmTranzilaPayment,
  dispatchTranzilaOverlayChange,
  fetchTranzilaPaymentStatus,
  pollTranzilaPaymentStatus,
  writeTranzilaPendingCharge,
  hasChargeSucceeded,
  invalidateThtk,
  isCustomerPaymentCancellation,
  isHandshakeExpired,
  isPayableChargeResponse,
  isThtkConsumed,
  googlePayEventSaysUnavailable,
  loadTranzilaApplePay,
  loadTranzilaHostedFields,
  logTxnIdOnly,
  markChargeSucceeded,
  missingHostedFieldsKeys,
  popupChargeError,
  reportTranzilaClientError,
  resetTranzilaChargeSession,
  selectedMethodAllowed,
  teardownTranzilaSession,
} from "@/modules/tranzilaHostedFields.js";

const GOOGLE_PAY_ENABLE_WAIT_MS = 8000;
const GOOGLE_PAY_UNAVAILABLE_HE = "Google Pay אינו זמין בדפדפן זה";

export default {
  name: "tranzila-hosted-fields-form",
  components: {
    ButtonComponent,
    FormFieldComponent,
    LoadingIndicator,
  },
  props: {
    content: Object,
    popupTitleId: String,
  },
  emits: ["close"],
  data() {
    return {
      loading: true,
      busy: false,
      chargeLocked: false,
      verifyingPayment: false,
      popupError: "",
      blockedMessage: "",
      googlePayAvailable: false,
      googlePayUnavailable: false,
      walletChargeStarted: false,
      walletRetryAvailable: false,
      fields: null,
      fieldValidity: {},
      fieldErrors: {
        number: "",
        credit_card_number: "",
        cvv: "",
        expiry: "",
      },
    };
  },
  computed: {
    hf() {
      return this.content?.form?.hosted_fields || {};
    },
    verifyingMessage() {
      return PAYMENT_VERIFYING_HE;
    },
    selectedMethod() {
      return this.hf.selected_method;
    },
    isCard() {
      return this.selectedMethod === "card";
    },
    isApple() {
      return this.selectedMethod === "apple";
    },
    isGoogle() {
      return this.selectedMethod === "google";
    },
    waitMessage() {
      return this.content?.wait_for_complete || "";
    },
    googleUnavailableMessage() {
      return GOOGLE_PAY_UNAVAILABLE_HE;
    },
    showWalletWait() {
      if (!this.waitMessage) {
        return false;
      }
      if (this.isApple) {
        return this.busy;
      }
      if (this.isGoogle) {
        if (this.showGooglePayButton || this.walletRetryAvailable) {
          return false;
        }
        return true;
      }
      return true;
    },
    showApplePayButton() {
      return this.isApple && !this.busy && !this.chargeLocked && !this.walletRetryAvailable;
    },
    showGooglePayButton() {
      return (
        this.isGoogle &&
        this.googlePayAvailable &&
        !this.googlePayUnavailable &&
        !this.busy &&
        !this.chargeLocked &&
        !this.walletRetryAvailable
      );
    },
    showGoogleUnavailable() {
      return this.isGoogle && this.googlePayUnavailable && !this.busy && !this.walletRetryAvailable;
    },
    formFields() {
      return Array.isArray(this.content?.form?.fields) ? this.content.form.fields : [];
    },
    extraFormFields() {
      return this.formFields.filter((field) => field?.type !== "hosted_field");
    },
    hostedFieldsByType() {
      const byType = {};
      this.formFields.forEach((field) => {
        if (field?.type === "hosted_field" && field.field_type) {
          byType[field.field_type] = field;
        }
      });
      return byType;
    },
    numberFieldId() {
      return this.hostedFieldElementId("credit_card_number", "tranzila-number");
    },
    expiryFieldId() {
      return this.hostedFieldElementId("expiry", "tranzila-expiry");
    },
    cvvFieldId() {
      return this.hostedFieldElementId("cvv", "tranzila-cvv");
    },
    iconsStrip() {
      const strip = this.content?.icons_strip;
      if (!Array.isArray(strip) || strip.length === 0) {
        return [];
      }
      return strip.filter((item) => item && typeof item === "object" && typeof item.src === "string" && item.src.trim() !== "");
    },
  },
  mounted() {
    this.bootstrap();
  },
  beforeUnmount() {
    this._disposed = true;
    this.clearGooglePayEnableTimeout();
    this.stopPaymentStatusPoll();
    dispatchTranzilaOverlayChange(false);
    teardownTranzilaSession(this.hf.thtk);
    this.fields = null;
  },
  methods: {
    fieldClass(name) {
      const state = this.fieldValidity[name];
      if (state === false) {
        return "hosted-fields-invalid";
      }
      if (state === true) {
        return "hosted-fields-valid";
      }
      return "";
    },
    hostedFieldDef(fieldType) {
      return this.hostedFieldsByType[fieldType] || null;
    },
    hostedFieldLabel(fieldType, fallback) {
      return this.hostedFieldDef(fieldType)?.label || fallback;
    },
    hostedFieldRequired(fieldType) {
      const field = this.hostedFieldDef(fieldType);
      if (field && Object.prototype.hasOwnProperty.call(field, "required")) {
        return !!field.required;
      }
      return true;
    },
    hostedFieldSelector(fieldType, fallbackId) {
      const selector = this.hostedFieldDef(fieldType)?.selector;
      if (typeof selector === "string" && selector.startsWith("#") && selector.length > 1) {
        return selector;
      }
      return `#${fallbackId}`;
    },
    hostedFieldElementId(fieldType, fallbackId) {
      return this.hostedFieldSelector(fieldType, fallbackId).slice(1);
    },
    setPopupError(message) {
      this.popupError = message || "";
      const el = document.getElementById("popup-error-message");
      if (el && el !== this.$el?.querySelector?.("#popup-error-message")) {
        el.innerHTML = message || "";
      }
    },
    clearFieldErrors() {
      this.fieldErrors = {
        number: "",
        credit_card_number: "",
        cvv: "",
        expiry: "",
      };
    },
    applyFieldErrors(err) {
      const messages = err?.messages || err?.errors || [];
      if (!Array.isArray(messages)) {
        return;
      }
      messages.forEach((item) => {
        const param = item?.param;
        const message = item?.message || "";
        if (param && Object.prototype.hasOwnProperty.call(this.fieldErrors, param)) {
          this.fieldErrors[param] = message;
        } else if (message) {
          this.setPopupError(message);
        }
      });
    },
    guardCharge() {
      if (hasChargeSucceeded() || this.chargeLocked) {
        this.setPopupError(CONTACT_SUPPORT_HE);
        return false;
      }
      if (isThtkConsumed(this.hf.thtk) || isHandshakeExpired(this.hf)) {
        this.setPopupError(HANDSHAKE_RETRY_HE);
        this.blockedMessage = HANDSHAKE_RETRY_HE;
        teardownTranzilaSession(this.hf.thtk);
        return false;
      }
      if (!canChargeHostedFields(this.hf)) {
        if (!selectedMethodAllowed(this.hf)) {
          this.setPopupError(METHOD_NOT_ALLOWED_HE);
          this.blockedMessage = METHOD_NOT_ALLOWED_HE;
          return false;
        }
        this.setPopupError(MISSING_FIELDS_HE);
        return false;
      }
      return true;
    },
    async bootstrap() {
      resetTranzilaChargeSession();
      const missing = missingHostedFieldsKeys(this.hf);
      if (missing.length) {
        this.loading = false;
        this.blockedMessage = MISSING_FIELDS_HE;
        this.setPopupError(MISSING_FIELDS_HE);
        return;
      }
      if (!selectedMethodAllowed(this.hf)) {
        this.loading = false;
        this.blockedMessage = METHOD_NOT_ALLOWED_HE;
        this.setPopupError(METHOD_NOT_ALLOWED_HE);
        return;
      }
      if (isHandshakeExpired(this.hf) || isThtkConsumed(this.hf.thtk)) {
        this.loading = false;
        this.blockedMessage = HANDSHAKE_RETRY_HE;
        this.setPopupError(HANDSHAKE_RETRY_HE);
        return;
      }
      try {
        await loadTranzilaHostedFields();
        if (this.selectedMethod === "apple") {
          if (!window.ApplePaySession) {
            this.loading = false;
            this.blockedMessage = METHOD_NOT_ALLOWED_HE;
            this.setPopupError(METHOD_NOT_ALLOWED_HE);
            return;
          }
          await loadTranzilaApplePay();
        }
        await this.$nextTick();
        await this.$nextTick();
        this.createFields();
        dispatchTranzilaOverlayChange(true);
      } catch (err) {
        console.error("Tranzila Hosted Fields load failed", err);
        this.loading = false;
        this.blockedMessage = "אירעה שגיאה בטעינת מסך התשלום";
        this.setPopupError(this.blockedMessage);
      }
    },
    createFields() {
      if (!window.TzlaHostedFields) {
        this.loading = false;
        this.blockedMessage = "אירעה שגיאה בטעינת מסך התשלום";
        this.setPopupError(this.blockedMessage);
        return;
      }
      const hostedFields = {
        credit_card_number: {
          selector: this.hostedFieldSelector("credit_card_number", "tranzila-number"),
          placeholder: "•••• •••• •••• ••••",
          tabindex: 1,
        },
      };
      // A CVV iframe makes Tranzila allow only CRYPTOGRAM_3DS, so desktop Chrome never gets googleIsEnable.
      if (!this.isGoogle) {
        hostedFields.cvv = {
          selector: this.hostedFieldSelector("cvv", "tranzila-cvv"),
          placeholder: "CVV",
          tabindex: 3,
        };
      }
      hostedFields.expiry = {
        selector: this.hostedFieldSelector("expiry", "tranzila-expiry"),
        placeholder: "MM/YY",
        version: String(this.hostedFieldDef("expiry")?.version ?? "1"),
        tabindex: 2,
      };
      this.fields = window.TzlaHostedFields.create({
        sandbox: false,
        fields: hostedFields,
        styles: {
          input: {
            "font-family": "RSRegular, system-ui, sans-serif",
            "font-size": "16px",
            "line-height": "40px",
            color: "#171717",
            height: "40px",
            width: "100%",
            margin: "0",
            padding: "0 8px",
            border: "0",
            outline: "none",
            "box-sizing": "border-box",
            display: "block",
            direction: "ltr",
          },
        },
      });
      if (this.fields?.onEvent) {
        this.fields.onEvent("ready", () => {
          this.loading = false;
          this.bindFieldFocus();
          if (this.selectedMethod === "bit") {
            this.startSelectedMethodCharge();
          }
          if (this.selectedMethod === "google") {
            this.armGooglePayEnableTimeout();
          }
        });
        this.fields.onEvent("validityChange", (event) => {
          const field = event?.field || event?.name;
          if (field) {
            this.fieldValidity = { ...this.fieldValidity, [field]: event.valid };
          }
        });
        this.fields.onEvent("cardTypeChange", () => {});
        this.fields.onEvent("focus", () => {});
        this.fields.onEvent("blur", () => {});
        this.fields.onEvent("submit", () => {
          if (this.isCard) {
            this.onCardSubmit();
          }
        });
        this.fields.onEvent("googleIsEnable", (event, extra) => {
          this.onGoogleIsEnable(event, extra);
        });
      } else {
        this.loading = false;
        if (this.selectedMethod === "bit") {
          this.startSelectedMethodCharge();
        }
        if (this.selectedMethod === "google") {
          this.armGooglePayEnableTimeout();
        }
      }
      this.bindFieldFocus();
    },
    bindFieldFocus() {
      [this.numberFieldId, this.expiryFieldId, this.cvvFieldId].forEach((id) => {
        const el = document.getElementById(id);
        if (!el) {
          return;
        }
        const iframe = el.querySelector("iframe");
        if (iframe) {
          iframe.setAttribute("height", "42");
          iframe.setAttribute("scrolling", "no");
        }
        if (el.dataset.hfFocusBound) {
          return;
        }
        el.dataset.hfFocusBound = "1";
        el.addEventListener("click", () => {
          const frame = el.querySelector("iframe");
          if (frame && typeof frame.focus === "function") {
            frame.focus();
          }
        });
      });
    },
    popupTextField(field) {
      return {
        ...field,
        in_popup: true,
        desktopClass: "col-12",
        wide: true,
        validate_on_submit: true,
      };
    },
    onCardSubmit() {
      if (!this.isCard || !this.fields?.charge) {
        return;
      }
      this.chargeSelected("card");
    },
    validateExtraFields() {
      const form = this.$refs.hfForm;
      if (!form || !this.extraFormFields.length) {
        return true;
      }
      let ok = true;
      const detail = {
        markInvalid() {
          ok = false;
        },
        focusTarget: null,
      };
      form.dispatchEvent(new CustomEvent("yummi-validate", { detail }));
      if (!ok) {
        detail.focusTarget?.focus?.();
      }
      return ok;
    },
    onApplePayClick() {
      if (!this.isApple) {
        return;
      }
      this.chargeSelected("apple");
    },
    onGooglePayClick() {
      if (!this.isGoogle) {
        return;
      }
      this.chargeSelected("google");
    },
    onGoogleIsEnable(event, extra) {
      if (!this.isGoogle || this._disposed || this.walletChargeStarted || this.chargeLocked) {
        return;
      }
      this.clearGooglePayEnableTimeout();
      if (googlePayEventSaysUnavailable(event) || googlePayEventSaysUnavailable(extra)) {
        this.googlePayAvailable = false;
        this.googlePayUnavailable = true;
        return;
      }
      this.googlePayUnavailable = false;
      this.googlePayAvailable = true;
    },
    armGooglePayEnableTimeout() {
      if (!this.isGoogle || this.googlePayAvailable || this.googlePayUnavailable || this._googlePayEnableTimer) {
        return;
      }
      this._googlePayEnableTimer = setTimeout(() => {
        this._googlePayEnableTimer = null;
        if (this._disposed || !this.isGoogle || this.googlePayAvailable || this.walletChargeStarted) {
          return;
        }
        this.googlePayUnavailable = true;
      }, GOOGLE_PAY_ENABLE_WAIT_MS);
    },
    clearGooglePayEnableTimeout() {
      if (this._googlePayEnableTimer) {
        clearTimeout(this._googlePayEnableTimer);
        this._googlePayEnableTimer = null;
      }
    },
    startSelectedMethodCharge() {
      if (this.isCard || this.isGoogle || this.walletChargeStarted || this.chargeLocked) {
        return;
      }
      this.chargeSelected(this.selectedMethod);
    },
    retryWalletCharge() {
      this.walletRetryAvailable = false;
      if (this.isGoogle) {
        this.chargeSelected("google");
        return;
      }
      this.startSelectedMethodCharge();
    },
    chargeApiReady(paymentMethod) {
      if (paymentMethod === "card") {
        return typeof this.fields?.charge === "function";
      }
      if (paymentMethod === "bit") {
        return typeof this.fields?.chargeBit === "function";
      }
      if (paymentMethod === "google") {
        return typeof this.fields?.chargeGpay === "function";
      }
      if (paymentMethod === "apple") {
        return typeof window.hfChargeApple === "function";
      }
      return false;
    },
    chargeSelected(paymentMethod) {
      if (!this.guardCharge()) {
        return;
      }
      if (paymentMethod !== this.selectedMethod) {
        this.setPopupError(METHOD_NOT_ALLOWED_HE);
        return;
      }
      if (!this.chargeApiReady(paymentMethod)) {
        this.setPopupError(METHOD_NOT_ALLOWED_HE);
        this.blockedMessage = METHOD_NOT_ALLOWED_HE;
        return;
      }
      if (!this.validateExtraFields()) {
        return;
      }
      const idResult = readCardHolderIdFromForm(this.$refs.hfForm, this.formFields);
      if (!idResult.ok) {
        return;
      }
      const payload = chargeParamsForMethod(this.hf, paymentMethod);
      if (paymentMethod === "card" && idResult.cardHolderId) {
        const idField = this.formFields.find((field) => field?.validate === "israeli_id");
        payload[idField?.data_name || "card_holder_id_number"] = idResult.cardHolderId;
      }
      const onDone = (err, response) => {
        this.handleChargeResult(paymentMethod, err, response);
      };
      const markChargeStarted = () => {
        this.clearFieldErrors();
        this.busy = true;
        this.walletRetryAvailable = false;
        if (!this.isCard) {
          this.walletChargeStarted = true;
        }
        dispatchTranzilaOverlayChange(true);
        this.beginPaymentStatusPoll();
      };
      if (paymentMethod === "card") {
        markChargeStarted();
        this.fields.charge(payload, onDone);
        return;
      }
      if (paymentMethod === "bit") {
        markChargeStarted();
        this.fields.chargeBit(payload, onDone);
        return;
      }
      if (paymentMethod === "google") {
        markChargeStarted();
        this.fields.chargeGpay(payload, onDone);
        return;
      }
      try {
        window.hfChargeApple(payload, onDone);
      } catch (appleErr) {
        this.handleChargeResult(paymentMethod, appleErr, null);
        return;
      }
      markChargeStarted();
    },
    async handleChargeResult(paymentMethod, err, response) {
      if (this._statusRedirected) {
        return;
      }
      if (err || !isPayableChargeResponse(response)) {
        this.stopPaymentStatusPoll();
        const status = await fetchTranzilaPaymentStatus(this.hf?.status_url);
        if (this._statusRedirected || status?.redirected) {
          this.completeStatusRedirect();
          return;
        }
        clearTranzilaPendingCharge();
        this.busy = false;
        this.walletChargeStarted = false;
        if (!this.isCard && !this.chargeLocked) {
          this.walletRetryAvailable = true;
        }
        if (err) {
          this.applyFieldErrors(err);
          if (!this.popupError) {
            this.setPopupError(CHARGE_FAILED_HE);
          }
        } else {
          const txnError = popupChargeError(response?.transaction_response?.error);
          this.setPopupError(txnError || CHARGE_FAILED_HE);
          this.applyFieldErrors(response);
        }
        if (isHandshakeExpired(this.hf)) {
          this.walletRetryAvailable = false;
          this.blockedMessage = HANDSHAKE_RETRY_HE;
          this.setPopupError(HANDSHAKE_RETRY_HE);
          teardownTranzilaSession(this.hf.thtk);
        }
        this.reportChargeFailure(paymentMethod, err, response);
        return;
      }
      this.busy = false;
      markChargeSucceeded();
      this.chargeLocked = true;
      logTxnIdOnly(response);
      invalidateThtk(this.hf.thtk);
      try {
        const result = await confirmTranzilaPayment({
          confirmUrl: this.hf.confirm_url,
          flow: this.hf.flow,
          paymentMethod,
          tranzilaResponse: response,
        });
        if (this._statusRedirected) {
          return;
        }
        this.handleConfirmResult(result);
      } catch (confirmErr) {
        if (this._statusRedirected) {
          return;
        }
        console.error("tranzila_confirm failed", confirmErr);
        this.enterPaymentVerification(null);
        this.reportClientError("confirm_failed", paymentMethod, confirmErr, response);
      }
    },
    handleConfirmResult(result) {
      if (this._statusRedirected) {
        return;
      }
      if (result?.redirected) {
        this._statusRedirected = true;
        this._awaitingLateApproval = false;
        this.stopPaymentStatusPoll();
        clearTranzilaPendingCharge();
        this.$emit("close");
        return;
      }
      if (result?.do_not_retry || result?.charge_captured) {
        this.enterPaymentVerification(result);
        return;
      }
      if (result?.conflict) {
        this.setPopupError(result.error_message || CHARGE_FAILED_HE);
        this.showConfirmPopup(result);
        return;
      }
      if (result?.error || result?.message) {
        this.enterPaymentVerification(result);
        if (result?.error) {
          this.reportClientError("confirm_failed", this.selectedMethod, { message: result.message }, null);
        }
        return;
      }
      this.chargeLocked = true;
      this.setPopupError(CONFIRM_FAILED_HE);
      this.blockedMessage = CONTACT_SUPPORT_HE;
    },
    beginPaymentStatusPoll() {
      const hf = this.hf || {};
      const statusUrl = typeof hf.status_url === "string" ? hf.status_url.trim() : "";
      if (!statusUrl) {
        return;
      }
      const startedAt = Date.now();
      writeTranzilaPendingCharge(hf, startedAt);
      const token = this.stopPaymentStatusPoll();
      this._statusPollRunning = true;
      this._statusPollResult = null;
      pollTranzilaPaymentStatus({
        statusUrl,
        intervalMs: hf.status_poll_interval_ms,
        timeoutMs: hf.status_poll_timeout_ms,
        startedAt,
        isCancelled: () => this._statusPollToken !== token,
      }).then((result) => {
        if (result?.redirected) {
          this._statusPollRunning = false;
          this.completeStatusRedirect();
          return;
        }
        if (this._statusPollToken !== token) {
          return;
        }
        this._statusPollRunning = false;
        this._statusPollResult = result;
        if (result?.timedOut) {
          if (this._awaitingLateApproval) {
            this.finishLateApprovalTimeout();
          } else {
            clearTranzilaPendingCharge();
          }
        }
      });
    },
    stopPaymentStatusPoll() {
      this._statusPollToken = (this._statusPollToken || 0) + 1;
      return this._statusPollToken;
    },
    enterPaymentVerification(result) {
      const statusUrl = typeof this.hf?.status_url === "string" ? this.hf.status_url.trim() : "";
      if (!statusUrl) {
        this.showLateApprovalFailure(result);
        return;
      }
      this.chargeLocked = true;
      this.walletRetryAvailable = false;
      this.busy = false;
      this.verifyingPayment = true;
      this.blockedMessage = "";
      this.setPopupError("");
      this._lateApprovalResult = result;
      this._awaitingLateApproval = true;
      if (this._statusPollResult?.timedOut) {
        this.finishLateApprovalTimeout();
        return;
      }
      if (!this._statusPollRunning) {
        this.beginPaymentStatusPoll();
      }
    },
    finishLateApprovalTimeout() {
      this.showLateApprovalFailure(this._lateApprovalResult);
    },
    showLateApprovalFailure(result) {
      this._awaitingLateApproval = false;
      this.verifyingPayment = false;
      clearTranzilaPendingCharge();
      if (result?.do_not_retry || result?.charge_captured) {
        this.applyChargeCapturedResult(result);
        this.reportClientError("status_timeout", this.selectedMethod, null, null);
        return;
      }
      this.chargeLocked = true;
      this.walletRetryAvailable = false;
      this.setPopupError(CONFIRM_FAILED_HE);
      this.blockedMessage = CONTACT_SUPPORT_HE;
      this.reportClientError("status_timeout", this.selectedMethod, null, null);
    },
    reportClientError(stage, paymentMethod, err, response) {
      try {
        reportTranzilaClientError(this.hf, {
          stage,
          paymentMethod,
          err,
          response,
        });
      } catch {
        /* reporting never affects payment */
      }
    },
    reportChargeFailure(paymentMethod, err, response) {
      try {
        if (isCustomerPaymentCancellation(err, response)) {
          return;
        }
        reportTranzilaClientError(this.hf, {
          stage: err ? "charge_error" : "not_approved",
          paymentMethod,
          err,
          response,
        });
      } catch {
        /* reporting never affects payment */
      }
    },
    completeStatusRedirect() {
      if (this._statusRedirected) {
        return;
      }
      this._statusRedirected = true;
      this._awaitingLateApproval = false;
      this.verifyingPayment = false;
      this.stopPaymentStatusPoll();
      clearTranzilaPendingCharge();
      teardownTranzilaSession(this.hf?.thtk);
      this.$emit("close");
    },
    applyChargeCapturedResult(result) {
      this.chargeLocked = true;
      this.walletRetryAvailable = false;
      const support = result?.error_message || CONTACT_SUPPORT_HE;
      this.setPopupError(support);
      this.blockedMessage = support;
      this.showConfirmPopup(result);
    },
    showConfirmPopup(result) {
      const popupContent = result?.prompt_json?.popup_content;
      if (popupContent && this.$store) {
        teardownTranzilaSession(this.hf.thtk);
        this.$store.dispatch("showPopup", popupContent);
        return;
      }
      if (result?.error_message) {
        this.setPopupError(result.error_message);
      }
    },
    onCancel() {
      this.stopPaymentStatusPoll();
      clearTranzilaPendingCharge();
      teardownTranzilaSession(this.hf.thtk);
      this.$emit("close");
    },
  },
};
</script>
<style scoped>
.popup-close-btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: #383838;
  line-height: inherit;
}

.required {
  color: red;
  margin-left: 5px;
}

.tranzila-hf {
  position: relative;
}

.tranzila-hf-field {
  position: relative;
  height: 42px;
  width: 100%;
  cursor: text;
  direction: ltr;
}

.tranzila-hf-field.hosted-fields-invalid {
  outline: 1px solid #ef3d49;
}

.tranzila-hf-field.hosted-fields-valid {
  outline: 1px solid #2e7d32;
}

.tranzila-hf-error:empty {
  display: none;
}

.tranzila-hf-card-fields-inert {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  pointer-events: none;
}

.tranzila-hf-wallet {
  position: relative;
  z-index: 1;
}

.tranzila-icons-strip {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: space-evenly;
  column-gap: 16px;
  row-gap: 8px;
  direction: ltr;
  margin-top: 28px;
  position: static;
  z-index: 0;
}

.tranzila-icons-strip-img {
  display: block;
  height: 24px;
  width: auto;
  flex: 0 0 auto;
  object-fit: contain;
  pointer-events: none;
}
</style>
<style>
iframe[src*="tranzila"],
iframe[src*="hf.tranzila.com"] {
  z-index: 4000;
}

.tranzila-hf-field iframe {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100% !important;
  height: 100% !important;
  border: 0;
  z-index: 1 !important;
  pointer-events: auto !important;
}

.tranzila-hf-card-fields-inert iframe,
.tranzila-hf-card-fields-inert button {
  pointer-events: none !important;
  z-index: 0 !important;
}
</style>
